import { Checkbox, Flex, Form, Input, Modal, Table } from 'antd';

import { ConfirmDeletingElement, Error403Unauthorized } from '@app/components';
import { AddWhatsAppIntegrationWarningModal } from '@app/components/Modals';
import {
  INTEGRATIONS,
  RIGHTS_INTEGRATIONS,
  RIGHTS_IS_DELETE,
  RIGHTS_IS_EDIT,
  U_KEY,
  WHATSAPP,
} from '@app/constants';
import {
  ERROR_REQUIRED_FIELD,
  MIN_SIX_SYMBOLS,
  NO_SPACE,
  NUMBERS_ONLY,
} from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { INTEGRATIONS_HEADER } from '@app/constants/tables';
import {
  PATTERN_EMAIL_VALIDATION,
  PATTERN_NUMBERS_ONLY,
  PATTERN_PASSWORD_VALIDATION_NO_SPACE,
} from '@app/constants/validationPatterns';
import { useAccessRights } from '@app/hooks';
import { useConfirm } from '@app/hooks/useConfirm';
import { AddAtcModal } from '@app/pages/Integrations/IntegrationsList/components/AddAtcModal';
import { EditMessengerModal } from '@app/pages/Integrations/IntegrationsList/components/EditMessengerModal';
import {
  LoyaltyChatSettingsModal,
  useLoyaltyChatSettingsLauncher,
} from '@app/pages/Integrations/IntegrationsList/components/LoyaltyChatSettingsModal';
import { YooKassaModal } from '@app/pages/Integrations/IntegrationsList/components/YooKassaModal';

import { Add1CIntegrationModal } from '@pages/Integrations/IntegrationsList/components/Add1CIntegrationModal';
import { ConnectIntegrationModal } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal';
import { Edit1CIntegrationModal } from '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal';
import { OAuthIntegrationFrameModal } from '@pages/Integrations/IntegrationsList/components/OAuthIntegrationFrameModal';
import { SalesFunnelFormItems } from '@pages/Integrations/IntegrationsList/components/SalesFunnelsFormItems';
import { SocialMediaSettingsModal } from '@pages/Integrations/IntegrationsList/components/SocialMediaSettingsModal';
import { WhatsAppAuthModalContent } from '@pages/Integrations/IntegrationsList/components/WhatsAppAuthModalContent';

import {
  useIntegrationLogRecording,
  useIntegrations,
} from '@pages/Integrations/IntegrationsList/hooks';
import { getWhatsAppAuthContentKey } from '@pages/Integrations/IntegrationsList/lib/getWhatsAppAuthContentKey';
import { WhatsAppAuthPhase } from '@pages/Integrations/IntegrationsList/lib/whatsAppAuthPhase';
import { adaptDataToTable } from '@pages/Integrations/IntegrationsList/utils/adaptDataToTable';

import type { FC } from 'react';

import '@pages/Integrations/IntegrationsList/styles/Integrations.desktop.scss';
import '@pages/Integrations/IntegrationsList/styles/Integrations.tablet.scss';

export const Integrations: FC = () => {
  const loyaltySettings = useLoyaltyChatSettingsLauncher();
  const {
    isLoading,
    integrations,
    handleDeleteIntegration,
    isWhatsAppModalOpen,
    setIsWhatsAppModalOpen,
    isEmailModalOpen,
    setIsEmailModalOpen,
    deletingModalOpen,
    setDeletingModalOpen,
    setCurrentDeletingId,
    clearAllIntervalsAndTimeouts,
    setUpdatingIntegrationId,
    updatingIntegrationId,
    isReloadModalOpen,
    setIsReloadModalOpen,
    handleRestartIntegration,
    isReloadLoading,
    setCurrentIntegrationType,
    isEditMessengerModalOpen,
    setIsEditMessengerModalOpen,
    editIntegrationForm,
    hasUserRightForModuleView,
    createEmailIntegrationForm,
    isEmailIntegrationLoading,
    submitEmailModalForm,
    handleEdit,
    setEditableIntegration,
    editableIntegration,
    salesFunnels,
    salesFunnelsStages,
    onEditEmailSalesFunnelChange,
    getData,
    setSelectedSalesFunnelId,
    currentAuthLink,
    createHubConnection,
    setIsOAuthFrameOpen,
    setCurrentAuthLink,
    showNotification,
    setInitialSalesFunnel,
    token,
    setToken,
    setIsOpenAddWhatsAppIntegrationWarningModal,
    isOpenAddWhatsAppIntegrationWarningModal,
    setIsSocialMediaSettingsModalOpen,
    openEmailHandlerParsingSettingsPage,
    openEditYooKassaModal,
    isAdd1CIntegrationModalOpen,
    setIsAdd1CIntegrationModalOpen,
    isEdit1CIntegrationModalOpen,
    setIsEdit1CIntegrationModalOpen,
    edit1CIntegrationId,
    setEdit1CIntegrationId,
    edit1CIntegrationMode,
    setEdit1CIntegrationMode,
    openAuthWhatsAppModal,
    whatsAppAuth,
    connectAuth,
    openConnectAuth,
    closeConnectAuth,
    activeBranchId,
  } = useIntegrations();

  // Журнал действий пишется всё время, пока открыт реестр; кнопка выгрузки
  // показывается филиалам с проблемными интеграциями и всей «Грушке»
  // (us/11024).
  useIntegrationLogRecording({ integrations });

  const { hasUserRight: hasUserRightToEditIntegration } = useAccessRights(
    RIGHTS_INTEGRATIONS,
    RIGHTS_IS_EDIT
  );
  const { hasUserRight: hasUserRightToDeleteIntegration } = useAccessRights(
    RIGHTS_INTEGRATIONS,
    RIGHTS_IS_DELETE
  );
  const { show: showConfirm } = useConfirm();

  const closeWhatsAppModal = () => {
    setIsWhatsAppModalOpen(false);
    clearAllIntervalsAndTimeouts();
    whatsAppAuth.close();
    setCurrentIntegrationType('');
  };

  const handleWhatsAppModalCancel = () => {
    const phase = whatsAppAuth.session?.phase;
    const isAuthorizationActive =
      phase !== undefined &&
      ![WhatsAppAuthPhase.Success, WhatsAppAuthPhase.Error].includes(phase);

    if (!isAuthorizationActive) {
      closeWhatsAppModal();

      return;
    }

    showConfirm({
      title: 'Закрыть подключение?',
      subtitle:
        'Отслеживание авторизации WhatsApp в этом окне остановится. Уже начатое подключение может завершиться в фоне — результат можно проверить, открыв интеграцию снова.',
      confirmText: 'Закрыть',
      cancelText: 'Продолжить',
      onConfirm: closeWhatsAppModal,
    });
  };

  if (!hasUserRightForModuleView) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}${INTEGRATIONS}`}>
      <Table
        loading={isLoading}
        columns={INTEGRATIONS_HEADER}
        className="integrations-table"
        rowClassName="integration-item"
        dataSource={adaptDataToTable(
          integrations || [],
          setDeletingModalOpen,
          setCurrentDeletingId,
          setUpdatingIntegrationId,
          setIsReloadModalOpen,
          handleEdit,
          hasUserRightToEditIntegration,
          hasUserRightToDeleteIntegration,
          setIsOAuthFrameOpen,
          setCurrentAuthLink,
          showNotification,
          openEmailHandlerParsingSettingsPage,
          openEditYooKassaModal,
          setEdit1CIntegrationId,
          setIsEdit1CIntegrationModalOpen,
          setEdit1CIntegrationMode,
          openAuthWhatsAppModal,
          openConnectAuth,
          loyaltySettings.canEdit
            ? loyaltySettings.openSettingsHandler
            : undefined
        )}
        rowKey={(record) => record[U_KEY]}
        pagination={false}
      />

      {loyaltySettings.integrationId !== null && (
        <LoyaltyChatSettingsModal
          key={loyaltySettings.integrationId}
          integrationId={loyaltySettings.integrationId}
          onCancel={loyaltySettings.closeSettingsHandler}
          onSaved={getData}
        />
      )}

      <Modal
        open={isWhatsAppModalOpen}
        onCancel={handleWhatsAppModalCancel}
        title="Добавить WhatsApp интеграцию"
        className="integrations-modal"
        footer={''}
      >
        {/* key сбрасывает состояние содержимого при смене аккаунта: иначе между
            открытиями и повторами переносятся код, режим входа по номеру и
            введённый телефон. Одного integrationId недостаточно: retry того же
            аккаунта создаёт новую generation и делает прежний pairing code
            недействительным (issue/11029). */}
        <WhatsAppAuthModalContent
          key={getWhatsAppAuthContentKey({
            sessionIntegrationId: whatsAppAuth.session?.integrationId,
            sessionGeneration: whatsAppAuth.session?.generation,
            fallbackIntegrationId: updatingIntegrationId,
          })}
          // Единственный источник кода — сессия авторизации. Прежде рядом
          // жило второе хранилище QR, и на экран возвращался недействительный
          // код из ответа создания интеграции (issue/11029).
          qr={whatsAppAuth.session?.qr ?? undefined}
          integrationId={
            whatsAppAuth.session?.integrationId ??
            (updatingIntegrationId || null)
          }
          activeBranchId={activeBranchId}
          phase={whatsAppAuth.session?.phase}
          errorMessage={whatsAppAuth.session?.errorMessage}
          onRetry={whatsAppAuth.retry}
        />
      </Modal>

      <SocialMediaSettingsModal
        getListData={getData}
        salesFunnels={salesFunnels}
        salesFunnelsStages={salesFunnelsStages}
        setSelectedSalesFunnelId={setSelectedSalesFunnelId}
        createHubConnection={createHubConnection}
        openWhatsAppAuthorization={whatsAppAuth.openNew}
        setInitialSalesFunnel={setInitialSalesFunnel}
        editableIntegration={editableIntegration}
        setEditableIntegration={setEditableIntegration}
        openConnectAuth={openConnectAuth}
      />

      <ConnectIntegrationModal
        isOpen={Boolean(connectAuth)}
        channelType={connectAuth?.channelType ?? ''}
        integrationId={connectAuth?.integrationId ?? null}
        activeBranchId={activeBranchId}
        authUrl={connectAuth?.authUrl ?? null}
        onCancel={closeConnectAuth}
      />

      <AddWhatsAppIntegrationWarningModal
        open={isOpenAddWhatsAppIntegrationWarningModal}
        onCancel={() => {
          setIsOpenAddWhatsAppIntegrationWarningModal(false);
          setCurrentIntegrationType('');
        }}
        onContinue={() => {
          setCurrentIntegrationType(WHATSAPP);
          setIsSocialMediaSettingsModalOpen(true);
          setIsOpenAddWhatsAppIntegrationWarningModal(false);
        }}
      />

      <OAuthIntegrationFrameModal currentAuthLink={currentAuthLink} />

      <Modal
        open={isReloadModalOpen}
        onCancel={() => {
          setIsReloadModalOpen(false);
          setUpdatingIntegrationId(0);
        }}
        onOk={() => {
          handleRestartIntegration();
        }}
        okButtonProps={{
          loading: isReloadLoading,
        }}
        cancelButtonProps={{
          disabled: isReloadLoading,
        }}
        okText="Перезагрузка"
        title="Перезагрузка интеграции"
      >
        Вы действительно хотите перезагрузить интеграцию?
      </Modal>

      <Modal
        open={isEmailModalOpen}
        onCancel={() => {
          setIsEmailModalOpen(false);
          createEmailIntegrationForm.resetFields();
          setEditableIntegration(undefined);
        }}
        onOk={createEmailIntegrationForm.submit}
        title={
          editableIntegration
            ? 'Редактировать интеграцию "обработка почты"'
            : 'Добавить интеграцию "обработка почты"'
        }
        className="modal add-email-integration-modal"
        okText="Сохранить"
        cancelText="Отменить"
        okButtonProps={{
          loading: isEmailIntegrationLoading,
        }}
        cancelButtonProps={{
          disabled: isEmailIntegrationLoading,
        }}
      >
        <Form
          form={createEmailIntegrationForm}
          className="modal-form"
          layout="vertical"
          onFinish={submitEmailModalForm}
        >
          <Form.Item
            name="SocialIntegrationName"
            label="Название"
            className="editIntegrationFormItem"
            initialValue={null}
          >
            <Input size="large" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="AccountLogin"
            className="editIntegrationFormItem"
            rules={[
              { required: true, message: ERROR_REQUIRED_FIELD },
              {
                pattern: PATTERN_EMAIL_VALIDATION,
                message: 'Не похоже на почту',
              },
            ]}
            initialValue={null}
          >
            <Input size="large" />
          </Form.Item>

          <Form.Item
            label="Пароль"
            name="Password"
            className="editIntegrationFormItem"
            rules={[
              {
                pattern: PATTERN_PASSWORD_VALIDATION_NO_SPACE,
                message: NO_SPACE,
              },
              {
                required: true,
                message: ERROR_REQUIRED_FIELD,
              },
              {
                min: 6,
                message: MIN_SIX_SYMBOLS,
              },
            ]}
            initialValue={null}
          >
            <Input.Password minLength={6} maxLength={60} size="large" />
          </Form.Item>

          <Form.Item
            label="Почтовый сервер (IMAP)"
            name="Server"
            className="editIntegrationFormItem"
            rules={[
              {
                required: true,
                message: ERROR_REQUIRED_FIELD,
              },
            ]}
            initialValue={null}
          >
            <Input maxLength={60} size="large" />
          </Form.Item>

          <Form.Item
            label="Порт"
            name="Port"
            className="editIntegrationFormItem"
            rules={[
              {
                required: true,
                message: ERROR_REQUIRED_FIELD,
              },
              {
                message: NUMBERS_ONLY,
                pattern: PATTERN_NUMBERS_ONLY,
              },
            ]}
            initialValue={null}
          >
            <Input size="large" maxLength={5} />
          </Form.Item>
          <Flex gap={12}>
            <Form.Item name="IsSsl" valuePropName="checked">
              <Checkbox>SSL</Checkbox>
            </Form.Item>

            <Form.Item name="ParseUsingNeural" valuePropName="checked">
              <Checkbox>Парсинг данных нейросетью</Checkbox>
            </Form.Item>
          </Flex>

          <SalesFunnelFormItems
            salesFunnels={salesFunnels}
            salesFunnelsStages={salesFunnelsStages}
            onSalesFunnelChange={onEditEmailSalesFunnelChange}
            errorRequiredField={ERROR_REQUIRED_FIELD}
            isIntegrationRegistryComponent={true}
          />
        </Form>
      </Modal>

      <ConfirmDeletingElement
        modalOpen={deletingModalOpen}
        setModalOpen={setDeletingModalOpen}
        handleDelete={handleDeleteIntegration}
        content="Вы уверены, что хотите удалить интеграцию?"
        title="Удаление интеграции"
      />

      <EditMessengerModal
        isEditMessengerModalOpen={isEditMessengerModalOpen}
        setIsEditMessengerModalOpen={setIsEditMessengerModalOpen}
        setEditableIntegration={setEditableIntegration}
        salesFunnels={salesFunnels}
        salesFunnelsStages={salesFunnelsStages}
        editIntegrationForm={editIntegrationForm}
        setSelectedSalesFunnelId={setSelectedSalesFunnelId}
        editableIntegration={editableIntegration}
        getData={getData}
        token={token}
        setToken={setToken}
      />

      <AddAtcModal
        setInitialSalesFunnel={setInitialSalesFunnel}
        setSelectedSalesFunnelId={setSelectedSalesFunnelId}
        salesFunnels={salesFunnels}
        salesFunnelsStages={salesFunnelsStages}
        getData={getData}
        editableIntegration={editableIntegration}
        token={token}
        setToken={setToken}
      />

      <YooKassaModal getData={getData} />

      <Add1CIntegrationModal
        isOpen={isAdd1CIntegrationModalOpen}
        onCancel={() => setIsAdd1CIntegrationModalOpen(false)}
        afterSave={getData}
      />

      <Edit1CIntegrationModal
        isOpen={isEdit1CIntegrationModalOpen}
        onCancel={() => setIsEdit1CIntegrationModalOpen(false)}
        afterSave={getData}
        integrationId={edit1CIntegrationId}
        initialMode={edit1CIntegrationMode}
      />
    </div>
  );
};
